/**
 * Blackjack Multiplayer Service
 * Handles Firestore operations for multiplayer tables
 */

import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  deleteDoc,
  onSnapshot,
  arrayUnion,
  serverTimestamp,
  query,
  where,
  orderBy,
  limit,
  increment,
  Unsubscribe,
} from 'firebase/firestore'
import { db } from '@/config/firebase'
import { shuffleArray } from '@/lib/utils'

export interface Card {
  suit: 'hearts' | 'diamonds' | 'clubs' | 'spades'
  value: string
}

export interface TablePlayer {
  odIdBrief: string
  name: string
  seat: number
  hand: Card[]
  currentBet: number
  status: 'waiting' | 'betting' | 'ready' | 'playing' | 'stand' | 'bust' | 'blackjack' | 'win' | 'lose' | 'push'
}

export interface ChatMessage {
  odIdBrief: string
  name: string
  text: string
  timestamp: string
}

export interface TableData {
  id: string
  name: string
  minBet: number
  maxPlayers: number
  capoId: string
  isPrivate: boolean
  password?: string
  players: TablePlayer[]
  spectators: string[]
  deck: Card[]
  dealerHand: Card[]
  gameState: 'waiting' | 'betting' | 'playing' | 'dealerTurn' | 'ended'
  currentTurnSeat: number | null
  messages: ChatMessage[]
  createdAt: Date
  lastActivity: Date
}

// Create a 6-deck shoe for blackjack
export function createDeck(): Card[] {
  const suits: Card['suit'][] = ['hearts', 'diamonds', 'clubs', 'spades']
  const values = ['A', '2', '3', '4', '5', '6', '7', '8', '9', '10', 'J', 'Q', 'K']
  const deck: Card[] = []

  for (let d = 0; d < 6; d++) {
    for (const suit of suits) {
      for (const value of values) {
        deck.push({ suit, value })
      }
    }
  }

  return shuffleArray(deck)
}

// Calculate hand value
export function calculateHandValue(hand: Card[]): number {
  if (!hand || hand.length === 0) return 0

  let value = 0
  let aces = 0

  for (const card of hand) {
    if (card.value === 'A') {
      aces++
      value += 11
    } else if (['K', 'Q', 'J'].includes(card.value)) {
      value += 10
    } else {
      value += parseInt(card.value)
    }
  }

  while (value > 21 && aces > 0) {
    value -= 10
    aces--
  }

  return value
}

// Get available tables
export async function getTables(): Promise<TableData[]> {
  const q = query(collection(db, 'tables'), where('isPrivate', '==', false))
  const snapshot = await getDocs(q)
  return snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() } as TableData))
}

// Create a new table
export async function createTable(
  name: string,
  minBet: number,
  capoId: string,
  capoName: string,
  isPrivate: boolean = false,
  password?: string
): Promise<string> {
  const tableRef = doc(collection(db, 'tables'))
  const tableData = {
    name,
    minBet,
    maxPlayers: 5,
    capoId,
    isPrivate,
    password: isPrivate ? password : null,
    players: [
      {
        odIdBrief: capoId,
        name: capoName,
        seat: 1,
        hand: [],
        currentBet: 0,
        status: 'waiting',
      },
    ],
    spectators: [],
    deck: [],
    dealerHand: [],
    gameState: 'waiting',
    currentTurnSeat: null,
    messages: [],
    createdAt: serverTimestamp(),
    lastActivity: serverTimestamp(),
  }

  await setDoc(tableRef, tableData)
  return tableRef.id
}

// Join a table as a player
export async function joinTable(
  tableId: string,
  odIdBrief: string,
  userName: string
): Promise<{ success: boolean; seat?: number; error?: string }> {
  const tableRef = doc(db, 'tables', tableId)
  const tableSnap = await getDoc(tableRef)

  if (!tableSnap.exists()) {
    return { success: false, error: 'Mesa no encontrada' }
  }

  const tableData = tableSnap.data() as TableData
  const players = tableData.players || []

  // Check if already in table
  if (players.some((p) => p.odIdBrief === odIdBrief)) {
    const existingPlayer = players.find((p) => p.odIdBrief === odIdBrief)
    return { success: true, seat: existingPlayer?.seat }
  }

  // Check if table is full
  if (players.length >= tableData.maxPlayers) {
    return { success: false, error: 'Mesa llena' }
  }

  // Find available seat
  const usedSeats = players.map((p) => p.seat)
  let seat = 1
  while (usedSeats.includes(seat)) {
    seat++
  }

  const newPlayer: TablePlayer = {
    odIdBrief,
    name: userName,
    seat,
    hand: [],
    currentBet: 0,
    status: 'waiting',
  }

  await updateDoc(tableRef, {
    players: [...players, newPlayer],
    lastActivity: serverTimestamp(),
  })

  return { success: true, seat }
}

// Join as spectator
export async function joinAsSpectator(tableId: string, odIdBrief: string): Promise<boolean> {
  const tableRef = doc(db, 'tables', tableId)

  try {
    await updateDoc(tableRef, {
      spectators: arrayUnion(odIdBrief),
      lastActivity: serverTimestamp(),
    })
    return true
  } catch (error) {
    console.error('Error joining as spectator:', error)
    return false
  }
}

// Leave table
export async function leaveTable(tableId: string, odIdBrief: string): Promise<void> {
  const tableRef = doc(db, 'tables', tableId)
  const tableSnap = await getDoc(tableRef)

  if (!tableSnap.exists()) return

  const tableData = tableSnap.data() as TableData
  const players = tableData.players.filter((p) => p.odIdBrief !== odIdBrief)
  const spectators = tableData.spectators.filter((s) => s !== odIdBrief)

  // If no players left, delete table
  if (players.length === 0) {
    await deleteDoc(tableRef)
    return
  }

  // If capo left, assign new capo
  let newCapoId = tableData.capoId
  if (tableData.capoId === odIdBrief) {
    newCapoId = players[0].odIdBrief
  }

  await updateDoc(tableRef, {
    players,
    spectators,
    capoId: newCapoId,
    lastActivity: serverTimestamp(),
  })
}

// Subscribe to table updates
export function subscribeToTable(
  tableId: string,
  callback: (data: TableData | null) => void
): Unsubscribe {
  const tableRef = doc(db, 'tables', tableId)
  return onSnapshot(tableRef, (snapshot) => {
    if (snapshot.exists()) {
      callback({ id: snapshot.id, ...snapshot.data() } as TableData)
    } else {
      callback(null)
    }
  })
}

// Start a new round (Capo only)
export async function startRound(tableId: string): Promise<void> {
  const tableRef = doc(db, 'tables', tableId)
  const tableSnap = await getDoc(tableRef)

  if (!tableSnap.exists()) return

  const tableData = tableSnap.data() as TableData
  const deck = createDeck()

  const players = tableData.players.map((p) => ({
    ...p,
    hand: [],
    currentBet: 0,
    status: 'betting' as const,
  }))

  await updateDoc(tableRef, {
    gameState: 'betting',
    deck,
    dealerHand: [],
    players,
    currentTurnSeat: null,
    lastActivity: serverTimestamp(),
  })
}

// Place a bet
export async function placeBet(
  tableId: string,
  odIdBrief: string,
  amount: number
): Promise<{ success: boolean; error?: string }> {
  const tableRef = doc(db, 'tables', tableId)
  const tableSnap = await getDoc(tableRef)

  if (!tableSnap.exists()) return { success: false, error: 'Mesa no encontrada' }

  const tableData = tableSnap.data() as TableData

  if (tableData.gameState !== 'betting') {
    return { success: false, error: 'No es momento de apostar' }
  }

  const players = tableData.players.map((p) => {
    if (p.odIdBrief === odIdBrief) {
      return { ...p, currentBet: amount, status: 'ready' as const }
    }
    return p
  })

  await updateDoc(tableRef, {
    players,
    lastActivity: serverTimestamp(),
  })

  return { success: true }
}

// Deal initial cards (Capo only, after all bets placed)
export async function dealCards(tableId: string): Promise<void> {
  const tableRef = doc(db, 'tables', tableId)
  const tableSnap = await getDoc(tableRef)

  if (!tableSnap.exists()) return

  const tableData = tableSnap.data() as TableData
  const deck = [...tableData.deck]

  // Deal 2 cards to each player
  const players = tableData.players.map((p) => {
    const card1 = deck.pop()!
    const card2 = deck.pop()!
    return {
      ...p,
      hand: [card1, card2],
      status: 'playing' as const,
    }
  })

  // Deal 2 cards to dealer
  const dealerHand = [deck.pop()!, deck.pop()!]

  // Find first player's turn (lowest seat)
  const seats = players.map((p) => p.seat).sort((a, b) => a - b)
  const firstTurn = seats[0]

  await updateDoc(tableRef, {
    gameState: 'playing',
    deck,
    dealerHand,
    players,
    currentTurnSeat: firstTurn,
    lastActivity: serverTimestamp(),
  })
}

// Player action: Hit
export async function hit(tableId: string, odIdBrief: string): Promise<void> {
  const tableRef = doc(db, 'tables', tableId)
  const tableSnap = await getDoc(tableRef)

  if (!tableSnap.exists()) return

  const tableData = tableSnap.data() as TableData
  const deck = [...tableData.deck]
  const newCard = deck.pop()!

  let nextTurn = tableData.currentTurnSeat
  let gameState = tableData.gameState

  const players = tableData.players.map((p) => {
    if (p.odIdBrief === odIdBrief) {
      const newHand = [...p.hand, newCard]
      const value = calculateHandValue(newHand)
      const isBust = value > 21

      if (isBust) {
        const result = getNextTurn(tableData.players, p.seat)
        nextTurn = result.nextSeat
        gameState = result.gameState
      }

      return {
        ...p,
        hand: newHand,
        status: isBust ? ('bust' as const) : ('playing' as const),
      }
    }
    return p
  })

  await updateDoc(tableRef, {
    deck,
    players,
    currentTurnSeat: nextTurn,
    gameState,
    lastActivity: serverTimestamp(),
  })
}

// Player action: Stand
export async function stand(tableId: string, odIdBrief: string): Promise<void> {
  const tableRef = doc(db, 'tables', tableId)
  const tableSnap = await getDoc(tableRef)

  if (!tableSnap.exists()) return

  const tableData = tableSnap.data() as TableData
  const player = tableData.players.find((p) => p.odIdBrief === odIdBrief)
  if (!player) return

  const players = tableData.players.map((p) => {
    if (p.odIdBrief === odIdBrief) {
      return { ...p, status: 'stand' as const }
    }
    return p
  })

  const result = getNextTurn(players, player.seat)

  await updateDoc(tableRef, {
    players,
    currentTurnSeat: result.nextSeat,
    gameState: result.gameState,
    lastActivity: serverTimestamp(),
  })
}

// Player action: Double
export async function double(tableId: string, odIdBrief: string): Promise<void> {
  const tableRef = doc(db, 'tables', tableId)
  const tableSnap = await getDoc(tableRef)

  if (!tableSnap.exists()) return

  const tableData = tableSnap.data() as TableData
  const player = tableData.players.find((p) => p.odIdBrief === odIdBrief)
  if (!player || player.hand.length !== 2) return

  const deck = [...tableData.deck]
  const newCard = deck.pop()!
  const newHand = [...player.hand, newCard]
  const handValue = calculateHandValue(newHand)
  const isBust = handValue > 21

  const players = tableData.players.map((p) => {
    if (p.odIdBrief === odIdBrief) {
      return {
        ...p,
        hand: newHand,
        currentBet: p.currentBet * 2,
        status: isBust ? ('bust' as const) : ('stand' as const),
      }
    }
    return p
  })

  const result = getNextTurn(players, player.seat)

  await updateDoc(tableRef, {
    deck,
    players,
    currentTurnSeat: result.nextSeat,
    gameState: result.gameState,
    lastActivity: serverTimestamp(),
  })
}

// Get next turn
function getNextTurn(
  players: TablePlayer[],
  currentSeat: number
): { nextSeat: number | null; gameState: TableData['gameState'] } {
  const seats = players.map((p) => p.seat).sort((a, b) => a - b)
  const currentIndex = seats.indexOf(currentSeat)

  for (let i = currentIndex + 1; i < seats.length; i++) {
    const player = players.find((p) => p.seat === seats[i])
    if (player && player.status === 'playing') {
      return { nextSeat: seats[i], gameState: 'playing' }
    }
  }

  return { nextSeat: null, gameState: 'dealerTurn' }
}

// Play dealer turn (Capo only)
export async function playDealerTurn(tableId: string): Promise<void> {
  const tableRef = doc(db, 'tables', tableId)
  const tableSnap = await getDoc(tableRef)

  if (!tableSnap.exists()) return

  const tableData = tableSnap.data() as TableData
  const deck = [...tableData.deck]
  let dealerHand = [...tableData.dealerHand]
  let dealerValue = calculateHandValue(dealerHand)

  // Dealer draws until 17
  while (dealerValue < 17) {
    dealerHand.push(deck.pop()!)
    dealerValue = calculateHandValue(dealerHand)

    await updateDoc(tableRef, {
      deck,
      dealerHand,
      lastActivity: serverTimestamp(),
    })

    await new Promise((resolve) => setTimeout(resolve, 800))
  }

  // Calculate results
  const finalPlayers = calculateResults(tableData.players, dealerValue, dealerHand)

  // Pay winners
  for (const player of finalPlayers) {
    if (['win', 'blackjack', 'push'].includes(player.status)) {
      const payout = calculatePayout(player)
      if (payout > 0) {
        await updateDoc(doc(db, 'users', player.odIdBrief), {
          balance: increment(payout),
        })
      }
    }
  }

  await updateDoc(tableRef, {
    players: finalPlayers,
    gameState: 'ended',
    lastActivity: serverTimestamp(),
  })
}

function calculatePayout(player: TablePlayer): number {
  switch (player.status) {
    case 'blackjack':
      return player.currentBet * 2.5
    case 'win':
      return player.currentBet * 2
    case 'push':
      return player.currentBet
    default:
      return 0
  }
}

function calculateResults(
  players: TablePlayer[],
  dealerValue: number,
  dealerHand: Card[]
): TablePlayer[] {
  const dealerBust = dealerValue > 21
  const dealerHasBlackjack = dealerHand.length === 2 && dealerValue === 21

  return players.map((p) => {
    if (p.status === 'bust') {
      return { ...p, status: 'lose' as const }
    }

    const playerValue = calculateHandValue(p.hand)
    const playerHasBlackjack = p.hand.length === 2 && playerValue === 21

    if (dealerHasBlackjack) {
      if (playerHasBlackjack) {
        return { ...p, status: 'push' as const }
      }
      return { ...p, status: 'lose' as const }
    }

    if (playerHasBlackjack) {
      return { ...p, status: 'blackjack' as const }
    }

    if (dealerBust) {
      return { ...p, status: 'win' as const }
    }

    if (playerValue > dealerValue) {
      return { ...p, status: 'win' as const }
    }

    if (playerValue === dealerValue) {
      return { ...p, status: 'push' as const }
    }

    return { ...p, status: 'lose' as const }
  })
}

// Send chat message
export async function sendChatMessage(
  tableId: string,
  odIdBrief: string,
  userName: string,
  text: string
): Promise<void> {
  const tableRef = doc(db, 'tables', tableId)

  await updateDoc(tableRef, {
    messages: arrayUnion({
      odIdBrief,
      name: userName,
      text,
      timestamp: new Date().toISOString(),
    }),
  })
}

// ============================================
// HIERARCHY-BASED TABLE MANAGEMENT
// ============================================

// Get all tables (for Patriarcas/DON - includes private tables)
export async function getAllTables(): Promise<TableData[]> {
  const snapshot = await getDocs(collection(db, 'tables'))
  return snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() } as TableData))
}

// Delete table with hierarchy permission check
export async function deleteTable(
  tableId: string,
  userId: string,
  userRole: 'gangster' | 'patriarch' | 'don'
): Promise<{ success: boolean; error?: string }> {
  try {
    const tableRef = doc(db, 'tables', tableId)
    const tableSnap = await getDoc(tableRef)

    if (!tableSnap.exists()) {
      return { success: false, error: 'Mesa no encontrada' }
    }

    const tableData = tableSnap.data() as TableData

    // Check permissions
    const isCapo = tableData.capoId === userId
    const canDelete = isCapo || userRole === 'patriarch' || userRole === 'don'

    if (!canDelete) {
      return { success: false, error: 'No tienes permiso para borrar esta mesa' }
    }

    // Log the action
    await logTableAction(tableId, userId, 'delete', {
      tableName: tableData.name,
      deletedBy: userRole,
    })

    // Delete the table
    await deleteDoc(tableRef)

    return { success: true }
  } catch (error) {
    console.error('Error deleting table:', error)
    return { success: false, error: 'Error al borrar la mesa' }
  }
}

// Kick player from table (Capo, Patriarch, or DON)
export async function kickPlayer(
  tableId: string,
  targetUserId: string,
  kickedBy: string,
  kickerRole: 'gangster' | 'patriarch' | 'don'
): Promise<{ success: boolean; error?: string }> {
  try {
    const tableRef = doc(db, 'tables', tableId)
    const tableSnap = await getDoc(tableRef)

    if (!tableSnap.exists()) {
      return { success: false, error: 'Mesa no encontrada' }
    }

    const tableData = tableSnap.data() as TableData

    // Check permissions - Capo can kick from their table, Patriarch/DON can kick from any
    const isCapo = tableData.capoId === kickedBy
    const canKick = isCapo || kickerRole === 'patriarch' || kickerRole === 'don'

    if (!canKick) {
      return { success: false, error: 'No tienes permiso para expulsar jugadores' }
    }

    // Can't kick the Capo unless you're Patriarch/DON
    if (targetUserId === tableData.capoId && kickerRole === 'gangster') {
      return { success: false, error: 'No puedes expulsar al Capo de la mesa' }
    }

    const players = tableData.players.filter((p) => p.odIdBrief !== targetUserId)
    const spectators = tableData.spectators.filter((s) => s !== targetUserId)

    // If kicking the Capo, assign new Capo
    let newCapoId = tableData.capoId
    if (targetUserId === tableData.capoId && players.length > 0) {
      newCapoId = players[0].odIdBrief
    }

    // If no players left, delete table
    if (players.length === 0) {
      await deleteDoc(tableRef)
      return { success: true }
    }

    await updateDoc(tableRef, {
      players,
      spectators,
      capoId: newCapoId,
      lastActivity: serverTimestamp(),
    })

    // Log the action
    await logTableAction(tableId, kickedBy, 'kick', {
      targetUserId,
      kickerRole,
    })

    return { success: true }
  } catch (error) {
    console.error('Error kicking player:', error)
    return { success: false, error: 'Error al expulsar jugador' }
  }
}

// Log table actions for audit
async function logTableAction(
  tableId: string,
  userId: string,
  action: string,
  details: Record<string, unknown>
): Promise<void> {
  try {
    const logRef = doc(collection(db, 'tableLogs'))
    await setDoc(logRef, {
      tableId,
      userId,
      action,
      details,
      timestamp: serverTimestamp(),
    })
  } catch (error) {
    console.error('Error logging table action:', error)
  }
}

// Get table logs (DON only)
export async function getTableLogs(limit_count: number = 100): Promise<unknown[]> {
  try {
    const q = query(
      collection(db, 'tableLogs'),
      orderBy('timestamp', 'desc'),
      limit(limit_count)
    )
    const snapshot = await getDocs(q)
    return snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }))
  } catch (error) {
    console.error('Error getting table logs:', error)
    return []
  }
}

// Check if user can create tables (membership approved)
export async function canCreateTable(userId: string): Promise<boolean> {
  try {
    const userRef = doc(db, 'users', userId)
    const userSnap = await getDoc(userRef)

    if (!userSnap.exists()) return false

    const userData = userSnap.data()
    return userData.membershipStatus === 'approved'
  } catch (error) {
    console.error('Error checking table creation permission:', error)
    return false
  }
}

// Get user role for permissions
export async function getUserRole(userId: string): Promise<'gangster' | 'patriarch' | 'don'> {
  try {
    const userRef = doc(db, 'users', userId)
    const userSnap = await getDoc(userRef)

    if (!userSnap.exists()) return 'gangster'

    const userData = userSnap.data()

    if (userData.isAdmin) return 'don'
    if (userData.isPatriarch) return 'patriarch'
    return 'gangster'
  } catch (error) {
    console.error('Error getting user role:', error)
    return 'gangster'
  }
}
